// Face ID / huella (passkeys) para el jugador. El servidor vive en routes/jugador_passkey.py:
// se activa con la sesión del PIN y después se entra sin PIN. La cara o el dedo nunca salen del
// celular: el servidor solo guarda la llave pública.
import axios from 'axios';
import { browserSupportsWebAuthn, platformAuthenticatorIsAvailable, startAuthentication, startRegistration } from '@simplewebauthn/browser';

// Canal propio (como useJugadorAuth): el interceptor global pone el admin_token en todas las consultas
const api = axios.create();
const BACKEND_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
const API = `${BACKEND_URL}/api/jugador/passkey`;

// En este navegador ya se activó (para destacar el botón y no volver a ofrecerlo)
const LLAVE_ACTIVO = 'faceid_activo';
const LLAVE_NO = 'faceid_ahora_no';

let _soportado: Promise<boolean> | null = null;
/** ¿Este celular / PC tiene Face ID, huella o Windows Hello? */
export function faceIdDisponible(): Promise<boolean> {
  if (!_soportado) {
    _soportado = browserSupportsWebAuthn()
      ? platformAuthenticatorIsAvailable().catch(() => false)
      : Promise.resolve(false);
  }
  return _soportado;
}

const leer = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const escribir = (k: string, v: string | null) => {
  try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* sin almacenamiento */ }
};

/** Ids de los jugadores que activaron Face ID en este navegador. */
const activos = (): string[] => (leer(LLAVE_ACTIVO) || '').split(',').filter(Boolean);
export const faceIdActivoAca = (jugadorId?: string) => (jugadorId ? activos().includes(jugadorId) : activos().length > 0);
export const marcarActivo = (jugadorId: string, si = true) =>
  escribir(LLAVE_ACTIVO, Array.from(new Set(si ? [...activos(), jugadorId] : activos().filter(x => x !== jugadorId))).join(',') || null);
export const dijoAhoraNo = (jugadorId: string) => (leer(LLAVE_NO) || '').split(',').includes(jugadorId);
export const marcarAhoraNo = (jugadorId: string) =>
  escribir(LLAVE_NO, Array.from(new Set([...(leer(LLAVE_NO) || '').split(',').filter(Boolean), jugadorId])).join(','));

/** Mensaje entendible para lo que puede fallar (cancelar, otro dispositivo, el servidor). */
export function errorFaceId(e: any): string {
  const n = e?.name || '';
  if (n === 'NotAllowedError' || n === 'AbortError') return 'Se canceló o no se pudo verificar. Si todavía no activaste Face ID en este celular, entrá con tu PIN y activalo.';
  if (n === 'InvalidStateError') return 'Este celular ya tiene Face ID activado para tu cuenta.';
  if (n === 'SecurityError') return 'Face ID solo funciona desde la app de Boliche Nicaragua.';
  return e?.response?.data?.detail || 'No se pudo usar Face ID. Entrá con tu PIN.';
}

/** Activa Face ID para el jugador con sesión (token del PIN). */
export async function activarFaceId(token: string, jugadorId: string): Promise<void> {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const { data } = await api.post(`${API}/registro/opciones`, {}, h);
  const credencial = await startRegistration({ optionsJSON: data.opciones });
  await api.post(`${API}/registro/verificar`, { reto: data.reto, credencial }, h);
  marcarActivo(jugadorId);
}

export interface SesionFaceId { access_token: string; jugador_id: string; jugador_nombre: string; telefono: string; }

/** Entra con Face ID (sin teléfono ni PIN): devuelve la misma sesión que el ingreso con PIN. */
export async function entrarConFaceId(): Promise<SesionFaceId> {
  const { data } = await api.post(`${API}/login/opciones`, {});
  const credencial = await startAuthentication({ optionsJSON: data.opciones });
  const r = await api.post(`${API}/login/verificar`, { reto: data.reto, credencial });
  marcarActivo(r.data.jugador_id);
  return r.data;
}

export interface Dispositivo { id: number; nombre: string; creada: string | null; ultimo_uso: string | null; }

export async function misDispositivos(token: string): Promise<Dispositivo[]> {
  const { data } = await api.get(`${API}/mis`, { headers: { Authorization: `Bearer ${token}` } });
  return data.dispositivos;
}

export async function quitarDispositivo(token: string, id: number): Promise<void> {
  await api.delete(`${API}/mis/${id}`, { headers: { Authorization: `Bearer ${token}` } });
}
